import React from "react";
import { motion } from "framer-motion";
import { TopCustomer } from "../types/dashboard";

interface TopCustomersProps {
  customers: TopCustomer[];
}

const AVATAR_PALETTES = [
  { bg: "bg-violet-100", text: "text-violet-700" },
  { bg: "bg-sky-100", text: "text-sky-700" },
  { bg: "bg-emerald-100", text: "text-emerald-700" },
  { bg: "bg-rose-100", text: "text-rose-700" },
  { bg: "bg-amber-100", text: "text-amber-700" },
];

const getInitials = (name?: string) => {
  if (!name) return "?";
  return name
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

const TopCustomers: React.FC<TopCustomersProps> = ({ customers }) => {
  if (!customers || customers.length === 0) {
    return (
      <div className="rounded-2xl bg-white/70 backdrop-blur-xl border border-gray-200 shadow-sm p-6">
        <p className="text-sm text-gray-400 text-center">No customer insights available</p>
      </div>
    );
  }

  const maxSpent = Math.max(...customers.map((c) => c.totalSpent));

  return (
    <div className="relative rounded-2xl bg-white/70 backdrop-blur-xl border border-gray-200 shadow-[0_8px_30px_rgba(0,0,0,0.06)] overflow-hidden">
      {/* Subtle Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-white/10 pointer-events-none" />

      {/* Header */}
      <div className="relative px-6 pt-6 pb-4 flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-gray-900">Top Customers</h2>
          <p className="text-xs text-gray-400 mt-1">Ranked by lifetime value</p>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-500">
          <span className="w-2 h-2 rounded-full bg-emerald-400" />
          Active
        </div>
      </div>

      <div className="border-t border-gray-100/80" />

      {/* List */}
      <div className="px-4 py-4 space-y-2">
        {customers.map((customer, index) => {
          const palette = AVATAR_PALETTES[index % AVATAR_PALETTES.length];

          const pct = maxSpent > 0 ? Math.round((customer.totalSpent / maxSpent) * 100) : 0;

          return (
            <motion.div
              key={customer.customerId}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ scale: 1.01 }}
              className="group flex items-center gap-4 px-4 py-3 rounded-xl bg-white/60 backdrop-blur border border-gray-200 hover:shadow-sm transition-all"
            >
              {/* Avatar */}
              <div className="relative shrink-0">
                <div className={`w-10 h-10 rounded-full ${palette.bg} ${palette.text} flex items-center justify-center text-sm font-semibold`}>{getInitials(customer.customerName)}</div>

                <span className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white border border-gray-200 text-gray-500 text-[10px] font-bold flex items-center justify-center shadow-sm">
                  {index + 1}
                </span>
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{customer.customerName || "Unknown Customer"}</p>

                {/* Progress */}
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.6 }} className="h-full rounded-full bg-gradient-to-r from-indigo-400 to-indigo-500" />
                  </div>
                  <span className="text-[10px] text-gray-400">{pct}%</span>
                </div>
              </div>

              {/* Stats */}
              <div className="text-right shrink-0">
                <p className="text-sm font-semibold text-gray-900">{customer.totalSpent.toLocaleString()}</p>
                <p className="text-xs text-gray-400">{customer.orderCount} orders</p>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50/70 border-t border-gray-100 text-center">
        <p className="text-xs text-gray-500">
          Total <span className="font-semibold text-gray-800">{customers.reduce((sum, c) => sum + c.totalSpent, 0).toLocaleString()}</span> across{" "}
          <span className="font-semibold text-gray-800">{customers.reduce((sum, c) => sum + c.orderCount, 0)}</span> orders
        </p>
      </div>
    </div>
  );
};

export default TopCustomers;
